/**
 * Model Management Component
 * Allows administrators to manage LLM models and API keys
 * Requirements: 11.2, 11.5
 */

import React, { useState, useEffect } from 'react';
import { Model } from '../types';

interface ApiKeyFormData {
  provider: string;
  apiKey: string;
}

const ModelManagement: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState<ApiKeyFormData>({
    provider: 'openai',
    apiKey: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch models on mount
  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/models', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }

      const data = await response.json();
      setModels(data.models || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModel = async (modelId: string, currentStatus: boolean) => {
    try {
      const response = await fetch(`/api/admin/models/${modelId}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ available: !currentStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to toggle model');
      }

      // Update local state
      setModels(prevModels =>
        prevModels.map(model =>
          model.id === modelId ? { ...model, available: !currentStatus } : model
        )
      );

      setSuccessMessage(`Model ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleApiKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!apiKeyForm.apiKey.trim()) {
      setError('API key is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch('/api/admin/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          provider: apiKeyForm.provider,
          apiKey: apiKeyForm.apiKey,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update API key');
      }

      setSuccessMessage(`API key for ${apiKeyForm.provider} updated successfully`);
      setApiKeyForm({ provider: 'openai', apiKey: '' });
      setShowApiKeyForm(false);
      
      // Refresh models to reflect any changes
      await fetchModels();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const getProviderColor = (provider: string): string => {
    const colors: Record<string, string> = {
      openai: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      anthropic: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      google: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      opensource: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    };
    return colors[provider] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading models...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Model Management
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage LLM models and API keys
            </p>
          </div>
          <button
            onClick={() => setShowApiKeyForm(!showApiKeyForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showApiKeyForm ? 'Cancel' : 'Add API Key'}
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* API Key Form */}
        {showApiKeyForm && (
          <form onSubmit={handleApiKeySubmit} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="provider"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Provider
              </label>
              <select
                id="provider"
                value={apiKeyForm.provider}
                onChange={(e) =>
                  setApiKeyForm({ ...apiKeyForm, provider: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                <option value="opensource">Open Source</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="apiKey"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                API Key
              </label>
              <input
                type="password"
                id="apiKey"
                value={apiKeyForm.apiKey}
                onChange={(e) =>
                  setApiKeyForm({ ...apiKeyForm, apiKey: e.target.value })
                }
                placeholder="Enter API key"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowApiKeyForm(false);
                  setApiKeyForm({ provider: 'openai', apiKey: '' });
                  setError(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Validating...' : 'Save API Key'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Models List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Available Models ({models.length})
          </h3>
        </div>

        {models.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No models available. Add API keys to enable models.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {models.map((model) => (
              <div
                key={model.id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-base font-medium text-gray-900 dark:text-white">
                        {model.name}
                      </h4>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getProviderColor(
                          model.provider
                        )}`}
                      >
                        {model.provider}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          model.available
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {model.available ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        <span className="font-medium">Context Window:</span>{' '}
                        {model.contextWindow.toLocaleString()} tokens
                      </div>
                      <div>
                        <span className="font-medium">Input Cost:</span> $
                        {(model.costPerToken.input ?? model.costPerToken.inputCostPer1M ?? 0).toFixed(2)}/1M tokens
                      </div>
                      <div>
                        <span className="font-medium">Output Cost:</span> $
                        {(model.costPerToken.output ?? model.costPerToken.outputCostPer1M ?? 0).toFixed(2)}/1M tokens
                      </div>
                    </div>
                  </div>

                  <div className="ml-6">
                    <button
                      onClick={() => handleToggleModel(model.id, model.available)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        model.available
                          ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
                          : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                      }`}
                    >
                      {model.available ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelManagement;
